import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import useSEO from "@/lib/useSEO";

export default function PaymentSuccess() {
  useSEO("Payment Received | Fleece The Regime", "Payment confirmation.", true);
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState({ status: "checking" });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error" });
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", orderNumber: data.order_number, total: data.total, tax: data.tax });
          clearCart();
          return;
        }
      } catch {
        /* keep polling */
      }
      if (++tries < 15) timer = setTimeout(poll, 2000);
      else setState({ status: "timeout" });
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main data-testid="payment-success-page" className="pt-16 min-h-screen flex items-center">
      <div className="px-6 sm:px-12 lg:px-20 py-24 max-w-4xl">
        {state.status === "checking" && (
          <>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-primary mb-6">Stripe</p>
            <h1 className="font-display uppercase text-6xl sm:text-7xl leading-[0.85] tracking-tighter">Confirming<br />your payment…</h1>
            <p className="mt-8 font-mono text-sm text-black/60">Hold tight. The money is changing hands.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-primary mb-6">Payment confirmed</p>
            <h1 className="font-display uppercase text-7xl sm:text-8xl leading-[0.85] tracking-tighter">Paid.<br />Loudly.</h1>
            <p className="mt-8 font-mono text-lg font-bold border-2 border-black inline-block px-6 py-3 shadow-hard" data-testid="paid-order-number">
              {state.orderNumber}
            </p>
            <p className="mt-8 max-w-md font-mono text-sm leading-relaxed text-black/70">
              Your order is paid and queued for the printer. You'll get a shipping note once it's on a truck.
            </p>
            {state.total != null && (
              <p className="mt-6 font-mono text-xs uppercase tracking-wider text-black/60" data-testid="paid-total-line">
                Total charged ${Number(state.total).toFixed(2)} — incl. tax ${Number(state.tax ?? 0).toFixed(2)}
              </p>
            )}
          </>
        )}
        {(state.status === "timeout" || state.status === "error") && (
          <>
            <h1 className="font-display uppercase text-6xl sm:text-7xl leading-[0.85] tracking-tighter">Still<br />confirming…</h1>
            <p className="mt-8 max-w-md font-mono text-sm leading-relaxed text-black/70">
              The bank is thinking about it. If you were charged, your order is safe — check back shortly.
            </p>
          </>
        )}
        <div className="mt-10">
          <Link
            to="/shop"
            data-testid="payment-success-shop-link"
            className="inline-block border-2 border-black bg-black px-8 py-4 font-display uppercase text-xl text-background shadow-hard btn-press hover:bg-primary"
          >
            Back to the catalog
          </Link>
        </div>
      </div>
    </main>
  );
}
